import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { articleContent } from '@/pages/article/articleData';
import { pillarArticles } from '@/mocks/articles';
import messages from '@/i18n/local/index';
import {
  applySeoHead,
  injectJsonLd,
  buildWebSiteSchema,
  buildArticleSchema,
  buildOrigin,
} from '@/lib/seo';

const BRAND = 'CSSBuy Spreadsheet';
const ARTICLE_KEYWORDS = 'CSSBuy, CSSBuy Spreadsheet';

type LangKey = 'en' | 'pt-br' | 'de';

const articleImages: Record<string, string> = {};
pillarArticles.forEach((a) => {
  articleImages[a.slug] = a.image;
});

interface ResolvedSeo {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type: 'website' | 'article';
  image?: string;
  inLanguage: string;
  langKey: LangKey;
  kind: 'home' | 'article';
  slug?: string;
}

function toHreflang(lang: LangKey): string {
  if (lang === 'pt-br') return 'pt-BR';
  if (lang === 'de') return 'de';
  return 'en';
}

function resolveRoute(pathname: string): ResolvedSeo | null {
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  let langKey: LangKey = 'pt-br';
  let rest = path;
  if (path === '/en' || path.startsWith('/en/')) {
    langKey = 'en';
    rest = path === '/en' ? '/' : path.slice(3);
  } else if (path === '/de' || path.startsWith('/de/')) {
    langKey = 'de';
    rest = path === '/de' ? '/' : path.slice(3);
  }

  const translation = messages[langKey]?.translation || messages.en.translation;

  if (rest === '/' || rest === '') {
    return {
      title: translation.meta_title,
      description: translation.meta_description,
      keywords: translation.meta_keywords,
      path,
      type: 'website',
      inLanguage: toHreflang(langKey),
      langKey,
      kind: 'home',
    };
  }

  const match = rest.match(/^\/article\/([^/]+)$/);
  if (match) {
    const slug = match[1];
    const langContent = articleContent[langKey] || articleContent.en;
    const article = langContent[slug] || langContent['cssbuy-spreadsheet-guide'];
    if (article) {
      return {
        title: article.title,
        description: article.metaDesc,
        keywords: ARTICLE_KEYWORDS,
        path,
        type: 'article',
        image: articleImages[slug],
        inLanguage: toHreflang(langKey),
        langKey,
        kind: 'article',
        slug,
      };
    }
  }

  return null;
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveRoute(location.pathname);

    if (!seo) {
      injectJsonLd('website-jsonld', null);
      injectJsonLd('article-jsonld', null);
      return;
    }

    applySeoHead({
      title: seo.title,
      description: seo.description,
      keywords: seo.keywords,
      path: seo.path,
      type: seo.type,
      image: seo.image,
    });

    const origin = buildOrigin();
    const url = origin ? `${origin}${seo.path === '/' ? '/' : seo.path}` : seo.path;

    if (seo.kind === 'article' && seo.slug) {
      const langContent = articleContent[seo.langKey] || articleContent.en;
      const article = langContent[seo.slug];
      injectJsonLd(
        'article-jsonld',
        article
          ? buildArticleSchema({
              headline: article.title,
              description: article.metaDesc,
              image: seo.image,
              author: article.author,
              publisherName: BRAND,
              dateModified: article.lastUpdated,
              url,
              inLanguage: seo.inLanguage,
            })
          : null,
      );
      injectJsonLd('website-jsonld', null);
    } else {
      injectJsonLd('article-jsonld', null);
      injectJsonLd(
        'website-jsonld',
        buildWebSiteSchema(BRAND, seo.description, url, seo.inLanguage),
      );
    }
  }, [location.pathname]);

  return null;
}